import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Marked, Token } from 'marked';
import { DocNavigationService } from '../../services/doc-navigation.service';

interface TocItem {
  id: string;
  text: string;
  depth: number;
}

@Component({
  selector: 'app-documentation',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './documentation.component.html',
  styleUrl: './documentation.component.scss'
})
export class DocumentationComponent implements OnInit, OnDestroy {
  docHtml: SafeHtml = '';
  private langSub?: Subscription;
  private readonly marked = new Marked();

  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private sanitizer: DomSanitizer,
    private navService: DocNavigationService
  ) {}

  ngOnInit(): void {
    const lang = this.translateService.currentLang || this.translateService.defaultLang || 'it';
    this.loadDoc(lang);
    this.langSub = this.translateService.onLangChange.subscribe(e => this.loadDoc(e.lang));
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  private loadDoc(lang: string): void {
    const supportedLangs = ['it', 'en'];
    const docLang = supportedLangs.includes(lang) ? lang : 'it';

    this.http.get(`/assets/docs/${docLang}.md`, { responseType: 'text' }).subscribe({
      next: (md) => {
        // 1. Otteniamo i token grezzi dal markdown
        const tokens = this.marked.lexer(md);

        // 2. Generiamo il menu dinamico filtrando solo i titoli
        let menuItems = tokens
        .filter((t: any) => t.type === 'heading' && t.depth <= 3)
        .map((t: any) => {
          const rawText = t.text || (t.tokens ? t.tokens.map((tk: any) => tk.text).join('') : '');
          const idMatch = rawText.match(/\{#([\w-]+)\}$/);

          return {
            id: idMatch ? idMatch[1] : this.slugify(rawText),
            text: rawText.replace(/\{#[\w-]+\}$/, '').trim(),
            depth: t.depth
          };
        });
        this.navService.updateMenu(menuItems);

        const sectionedHtml = this.wrapTokensInSections(tokens);
        this.docHtml = this.sanitizer.bypassSecurityTrustHtml(sectionedHtml);
      },
      error: (err) => console.error("Errore caricamento doc", err)
    });
  }

  scrollTo(id: string): void {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private wrapTokensInSections(tokens: any[]): string {
    let html = '';
    let sectionOpen = false;

    tokens.forEach((token, index) => {
      if (token.type === 'heading') {
        if (sectionOpen) html += `</section>\n`;

        // --- RECUPERO TESTO AGGRESSIVO ---
        let rawText = token.text || '';

        // Se 'text' è vuoto, cerchiamo nei sotto-token (comune in Marked v4+)
        if (!rawText && token.tokens) {
          rawText = token.tokens
            .map((t: any) => t.text || t.raw || '')
            .join('')
            .trim();
        }

        // DEBUG: Apri la console del browser (F12) e guarda cosa stampa qui
        console.log(`Token ${index} [H${token.depth}]:`, { rawText, token });

        // Gestione ID
        const idMatch = rawText.match(/\{#([\w-]+)\}$/);
        const cleanText = idMatch ? rawText.replace(/\{#[\w-]+\}$/, '').trim() : rawText;

        // Se cleanText è ancora vuoto, mettiamo un fallback temporaneo per vedere se cambia qualcosa
        const id = idMatch ? idMatch[1] : (this.slugify(cleanText) || `section-${index}`);

        // SCRIVIAMO L'ID (Usiamo le backtick per sicurezza)
        html += `<section id="${id}">\n`;
        html += `<h${token.depth}>${this.marked.parseInline(cleanText)}</h${token.depth}>\n`;

        sectionOpen = true;
      } else {
        if (!sectionOpen && token.type !== 'space') {
          html += `<section>\n`;
          sectionOpen = true;
        }
        if (token.type !== 'space') {
          html += this.marked.parser([token]);
        }
      }
    });

    if (sectionOpen) html += `</section>`;
    return html;
  }

  private slugify(text: string): string {
    if (!text) return '';
    return text
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Rimuove accenti
      .replace(/[^a-z0-9 -]/g, '')    // Rimuove caratteri speciali
      .replace(/\s+/g, '-')           // Spazi -> Trattini
      .replace(/-+/g, '-');           // Rimuove doppi trattini
  }
}
